import store from '@/store';
import user from '@/components/user';
import api, {
  localizedAndRegionalUrl,
  paginateAll,
  toGraphQLQuery,
  getGraphQLHeaders,
} from '@/api';
import slugify from 'slugify';
import offline from '@/offline';
import throttle from 'lodash.throttle';
import constants from '@/constants';
import log from '@/configured/logger';
import { resolveActivePkLanguage } from '@/configured/resolve-active-language';
import { localeToCraftSiteHandle } from '@/configured/languages/config';
import {
  isBootstrapRequest,
  normalizeBootstrapOptions,
  runSharedLoad,
} from '@/base-data/shared-load';

/**
 * Helper to execute a GraphQL query with automatic fallback to English
 * when the current language returns no data.
 */
function getActiveAppLanguage() {
  return resolveActivePkLanguage();
}

async function fetchWithFallback(queryBuilder, extractData, queryName) {
  const site = localeToCraftSiteHandle(getActiveAppLanguage());

  const executeQuery = (siteCode) => {
    const query = queryBuilder(siteCode);
    return api.get(constants.GRAPH_QL_ENDPOINT, {
      params: { query: toGraphQLQuery(query) },
      headers: getGraphQLHeaders(),
    });
  };

  try {
    const response = await executeQuery(site);
    const data = extractData(response);
    
    // If no data (or empty array) and not already English, fall back to English
    const isEmpty = !data || (Array.isArray(data) && data.length === 0);
    if (isEmpty && site !== 'en') {
      log.info(`No data for ${queryName} in ${site}, falling back to English`);
      const fallbackResponse = await executeQuery('en');
      return extractData(fallbackResponse);
    }
    
    return data;
  } catch (error) {
    if (api.isCancel(error)) {
      log.info(`Caught cancellation of ${queryName}`);
      return null;
    }
    
    // On error for non-English, try English as fallback
    if (site !== 'en') {
      log.info(`Error loading ${queryName} for ${site}, trying English fallback`);
      try {
        const fallbackResponse = await executeQuery('en');
        return extractData(fallbackResponse);
      } catch (fallbackError) {
        log.error(`Error loading ${queryName} fallback`, fallbackError);
        return null;
      }
    }
    
    log.error(`Error loading ${queryName}`, error);
    return null;
  }
}

let throttledLoadProductData = throttle(
  loadProductData,
  constants.OPTIMISTIC_CACHE_REFRESH,
);
let throttledLoadCategoryData = throttle(
  loadCategoryData,
  constants.OPTIMISTIC_CACHE_REFRESH,
);

function getCurrentRegion() {
  return store.getters[`${user.store.namespace}/regionSlug`]();
}

const exports = {
  namespace: 'products',
  module: {
    namespaced: true,
    state: {
      products: null,
      categories: null,
      // Naming this internal so future developers avoid using it directly
      internalProductCategories: null,
      menuProducts: null,
      productsOverview: null,
      loaded: false,
      hotLoadedProductDetails: null,
      excludedProducts: null,
    },
    mutations: {
      SET_PRODUCTS,
      SET_MENU_PRODUCTS: (state, menuProducts) => {
        state.menuProducts = menuProducts;
      },
      REPLACE_PRODUCTS,
      SET_CATEGORIES: (state, categories) => {
        state.categories = categories;
      },
      SET_PRODUCT_CATEGORIES: (state, productCategories) => {
        state.internalProductCategories = productCategories;
      },
      CLEAR_OFFLINE_DATA: (state) => {
        state.products = null;
        state.categories = null;
      },
      CLEAR_CACHED_PRODUCTS: (state) => {
        state.products = [];
        state.hotLoadedProductDetails = null; // Also clear individually loaded product details
      },
      REMOVE_PRODUCT,
      SET_PRODUCTS_OVERVIEW: (state, productsOverview) => {
        state.productsOverview = productsOverview;
      },
      SET_LOADED: (state, loaded) => {
        state.loaded = loaded;
      },
      SET_HOT_LOADED_PRODUCT_DETAILS,
      SET_EXCLUDED_PRODUCTS: (state, excludedProducts) => {
        state.excludedProducts = excludedProducts;
      },
    },
    actions: {
      loadProductDataIfNeeded,
      loadAll,
      getProducts,
      getCategories,
      removeProduct,
      clearOfflineData: ({ commit }) => {
        commit('CLEAR_OFFLINE_DATA');
      },
      clearCachedProducts,
      loadEmbeddedVideoMetadata,
      loadProductCategories,
      loadMenuProducts,
      loadProductsOverview,
      loadIndividualProductDataById,
      /** Slug for active locale only — no EN fallback (see fetchWithFallback + hot cache). */
      fetchCanonicalSlugForActiveLocale,
      loadExcludedProducts,
    },
    getters: {
      getProductById,
      hasMenuProductsInCategory,
      getMenuProductsByCategory,
      getProductsByCategory,
      getCategoryById,
      categoryUrlSlug,
      getRootParentTitleCategoryById,
      findProductsUsingAssetId,
      getFilteredCategories,
    },
  },
};

store.registerModule(exports.namespace.split('/'), exports.module);

export default exports;

//////////

async function loadProductDataIfNeeded({ state, commit }, payload = false) {
  const { forceInline, mode, bootstrapContext } =
    normalizeBootstrapOptions(payload);

  if (state.loaded && !forceInline) {
    if (isBootstrapRequest(bootstrapContext)) {
      return Promise.resolve();
    }

    log.info('loadProductDataIfNeeded: optimistic reload');
    store.dispatch(`${exports.namespace}/loadExcludedProducts`);
    store.dispatch(`${exports.namespace}/loadProductsOverview`);
    store.dispatch(`${exports.namespace}/loadProductCategories`);
    store.dispatch(`${exports.namespace}/loadMenuProducts`);
    return Promise.resolve();
  }

  log.info('loadProductDataIfNeeded: needed data');
  const language = store.state.i18n.language || 'en';

  return runSharedLoad(
    `${exports.namespace}:${language}`,
    () =>
      Promise.all([
        store.dispatch(`${exports.namespace}/loadExcludedProducts`),
        store.dispatch(`${exports.namespace}/loadProductsOverview`),
        store.dispatch(`${exports.namespace}/loadProductCategories`),
        store.dispatch(`${exports.namespace}/loadMenuProducts`),
      ]).then(async () => {
        await store.dispatch(`${exports.namespace}/loadAll`);
        commit('SET_LOADED', true);
      }),
    {
      bootstrapContext,
      metricKey: exports.namespace,
    }
  );
}

function SET_HOT_LOADED_PRODUCT_DETAILS(state, individualProductData) {
  if (!state.hotLoadedProductDetails) {
    state.hotLoadedProductDetails = {};
  }
  const language = individualProductData.language || store.state.i18n.language || 'en';
  if (!state.hotLoadedProductDetails[language]) {
    state.hotLoadedProductDetails[language] = {};
  }
  state.hotLoadedProductDetails[language][individualProductData.entry.id] =
    individualProductData.entry;
}

function SET_PRODUCTS(state, products) {
  if (!state.products) {
    state.products = products;
    return;
  }

  let newProducts = JSON.parse(JSON.stringify(state.products));

  // Update or add products
  products.forEach((newProduct) => {
    const index = newProducts.findIndex((p) => p.id === newProduct.id);
    const oldProduct = index < 0 ? null : newProducts[index];

    if (oldProduct) {
      const mergedProduct = Object.assign({}, oldProduct, newProduct);
      newProducts.splice(index, 1, mergedProduct);
    } else {
      newProducts.push(newProduct);
    }
  });

  // Finally remove missing product ids
  const newIds = products.map((p) => p.id);
  newProducts = newProducts.filter((p) => newIds.includes(p.id));

  if (JSON.stringify(newProducts) !== JSON.stringify(state.products)) {
    state.products = newProducts;
  }
}

function REPLACE_PRODUCTS(state, productsPayload) {
  if (!Array.isArray(productsPayload)) {
    log.error('[DEBUG] REPLACE_PRODUCTS - Payload was not an array. state.products not changed.', productsPayload);
    return;
  }

  if (!state.products) {
    state.products = []; // Initialize if it's null
  }

  log.debug(`[DEBUG] REPLACE_PRODUCTS - Starting. Current state.products count: ${state.products.length}. Payload count: ${productsPayload.length}`);

  productsPayload.forEach((newProduct) => {
    // Ensure IDs are compared as numbers for consistency
    const newProductId = Number(newProduct.id);
    const index = state.products.findIndex((p) => Number(p.id) === newProductId);

    const productToStore = { ...newProduct }; // Create a shallow copy to avoid unintended mutations of the source array objects
    productToStore.hasDetail = true;

    log.debug(`[DEBUG] REPLACE_PRODUCTS - Processing Product ID ${productToStore.id}. Has mediaCollections:`, productToStore.mediaCollections ? `YES (count: ${productToStore.mediaCollections.length})` : 'NO', productToStore.mediaCollections ? JSON.parse(JSON.stringify(productToStore.mediaCollections)) : undefined);

    if (index === -1) {
      // Product is new, add it
      state.products.push(productToStore);
      log.debug(`[DEBUG] REPLACE_PRODUCTS - Added new product ID ${productToStore.id}`);
    } else {
      // Product exists, replace it at the found index
      // This ensures the entire object is replaced with the new, fresh data including mediaCollections
      state.products.splice(index, 1, productToStore);
      log.debug(`[DEBUG] REPLACE_PRODUCTS - Replaced existing product ID ${productToStore.id} at index ${index}`);
    }
  });
  log.debug(`[DEBUG] REPLACE_PRODUCTS - Finished. New state.products count: ${state.products.length}`);
}

function loadAll(v) {
  const language = store.state.i18n.language || 'en';
  const region = getCurrentRegion();

  return runSharedLoad(
    `${exports.namespace}:all:${language}:${region}`,
    () => Promise.all([getCategories(v), getProducts(v)])
  );
}

function getProducts({ state, commit }) {
  if (!store.state[`${offline.store.namespace}`].isOnline) {
    log.info('Using offline products');
    return state.products !== null ? Promise.resolve() : Promise.reject();
  }

  // Optimistic loading
  if (state.products !== null && state.products.length > 0) {
    log.info(
      'Throttle products fire',
      new Date(),
      constants.OPTIMISTIC_CACHE_REFRESH,
    );
    throttledLoadProductData(commit, state);
    return Promise.resolve();
  } else {
    return loadProductData(commit, state);
  }
}

function clearCachedProducts({ state, commit }) {
  if (state.products !== null && state.products.length > 0) {
    log.info('Clearing cached products');
    commit('CLEAR_CACHED_PRODUCTS');
  }
}

function loadProductData(commit, state) {
  const language = store.state.i18n.language || 'en';
  const region = getCurrentRegion();
  let endpoint = localizedAndRegionalUrl('products');
  log.info('Loading products', new Date(), { endpoint });

  return runSharedLoad(`${exports.namespace}:payload:${language}:${region}`, () =>
    api
      .get(endpoint)
      .then(async (rsp) => {
        log.debug('Got products response', new Date(), { endpoint });
        if (!rsp.data) {
          log.error('No products returned', { endpoint });
          return;
        }
        log.info('Found products', { rsp, endpoint });
        commit('SET_PRODUCTS', rsp.data);
        await loadProductDetails(commit, state);
      })
      .catch((error) => {
        if (api.isCancel(error)) {
          log.info(`Caught cancellation of ${endpoint} request`);
          return;
        }
        log.error('Error refreshing products', error, state);
        if (!state.products) {
          log.info('No products found. Throwing error');
          throw error;
        }
      })
  );
}

async function loadProductDetails(commit, state) {
  const language = store.state.i18n.language || 'en';
  const queryBuilder = (site) => `{
    entries(type: "products", site: "${site}") {
      title
      id
      slug
      type: typeHandle
      dateCreated
      dateUpdated
      postDate
      ... on products_products_Entry {
        newProductCategory {
          title
          id
          slug
          url
          dateCreated
          dateUpdated
          level
          ... on productCategories_Category {
            mainImage {
              title
              id
              url
              dateCreated
              dateUpdated
              kind
              filename
              path
              size
            }
          }
        }
        mainImage {
          title
          id
          url
          dateCreated
          dateUpdated
          kind
          filename
          path
          size
        }
        tagLine
        overview
        flagAsNew
        specifications {
          ... on specifications_engines_Entry {
            alternator
            boreStroke
            colors
            controls
            coolingSystem
            customSpecifications {
              label
              value
            }
            cylinders
            dateCreated
            dateUpdated
            dimensions
            displacement
            emissions
            engineCounts {
              label
              number
            }
            fuelRequirements
            fuelSystem
            gearcase
            gearShift
            id
            ignition
            induction
            maxRPM
            midsection
            mountingCenters
            oilCapacity
            partNumber
            postDate
            power
            price
            propulsion
            protectionSystem
            recommendedOil
            repowerEnabled
            slug
            starting
            steering
            title
            turbo
            type: typeHandle
            weight
          }
        }
        features {
          ... on features_feature_BlockType {
            id
            type: typeHandle
            dateCreated
            dateUpdated
            heading
            body
            benefit
            media {
              title
              id
              url
              kind
              size
              mediaSquareUrl: url @transform (transform: "mediaSquare")
            }
          }
        }
        mediaCollections {
          ... on mediaCollections_collection_BlockType {
            id
            type: typeHandle
            dateCreated
            dateUpdated
            heading
            media {
              id
              title
              kind
              size
              url
              mediaSquareUrl: url @transform (transform: "mediaSquare")
              ... on remote_Asset {
                summary
                videoEmbed {
                  ... on videoEmbed_BlockType {
                    videoUrl
                    width
                    height
                  }
                }
                thumbnail {
                  id
                  title
                  url @transform (transform: "mediaSquare")
                }
              }
            }
          }
        }
      }
    }
  }`;

  log.info('Loading product details', new Date());

  return runSharedLoad(`${exports.namespace}:details:${language}`, async () => {
    const entries = await fetchWithFallback(
      queryBuilder,
      (response) => response.data.entries,
      'product details'
    );

    if (entries && Array.isArray(entries) && entries.length > 0) {
      log.debug('[DEBUG] loadProductDetails - Loaded product entries (count: ' + entries.length + ')');
      commit('REPLACE_PRODUCTS', entries);
    } else {
      log.error('[DEBUG] loadProductDetails - Did NOT commit REPLACE_PRODUCTS. Response structure issue or empty entries?');
    }
  });
}

function loadEmbeddedVideoMetadata({ commit, state }, videoData) {
  if (!store.state[`${offline.store.namespace}`].isOnline) {
    return Promise.reject();
  }

  const endpoint = videoData.url;
  return api
    .get(endpoint)
    .then((data) => {
      log.info('Loaded embedded video data', { data, endpoint });
      return data;
    })
    .catch((error) => {
      if (api.isCancel(error)) {
        log.info(`Caught cancellation of ${endpoint} request`);
        return;
      }
      log.error(
        `Error loading embedded video for ${videoData.url}`,
        error,
        state,
      );
      throw error;
    });
}

function getCategories({ state, commit }) {
  if (!store.state[`${offline.store.namespace}`].isOnline) {
    return state.categories !== null ? Promise.resolve() : Promise.reject();
  }

  // Optimistic loading
  if (state.categories !== null) {
    throttledLoadCategoryData(commit, state);
    return Promise.resolve();
  } else {
    return loadCategoryData(commit, state);
  }
}

function loadCategoryData(commit, state) {
  const language = store.state.i18n.language || 'en';
  const region = getCurrentRegion();
  const endpoint = localizedAndRegionalUrl('/categories');

  return runSharedLoad(`${exports.namespace}:categories:${language}:${region}`, () =>
    api
      .get(endpoint)
      .then((response) => {
        if (JSON.stringify(state.categories) !== JSON.stringify(response.data)) {
          commit('SET_CATEGORIES', response.data);
        } else {
          log.info('Categories matched, skipping replacement');
        }
      })
      .catch((error) => {
        if (api.isCancel(error)) {
          log.info(`Caught cancellation of ${endpoint} request`);
          return;
        }
        log.error('Error refreshing products', error, state);
        if (!state.categories) {
          log.info('No categories found. Throwing error');
          throw error;
        }
      })
  );
}

async function loadIndividualProductDataById({ commit, state }, { id }) {
  const language = resolveActivePkLanguage();
  const queryBuilder = (site) => `{
    entry(type: "products", site: "${site}", id: "${id}") {
      title
      id
      slug
      type: typeHandle
      dateCreated
      dateUpdated
      postDate
      ... on products_products_Entry {
      newProductCategory  {
          title
          id
          slug
          url 
          dateCreated
          dateUpdated
          level
        	... on productCategories_Category {
            mainImage {
              title
              id
              url
              dateCreated
              dateUpdated
              kind
              filename
              path
              size
            }
          }
        }
        mainImage {
          title
          id
          url
          dateCreated
          dateUpdated
        	kind
          filename
          path
          size
        }
        tagLine
        overview
        flagAsNew
        specifications {
          ... on specifications_engines_Entry {
            alternator
            boreStroke
            colors
            controls
            coolingSystem
            customSpecifications {
              label
              value
            }
            cylinders
            dateCreated
            dateUpdated
            dimensions
            displacement
            emissions
            engineCounts {
              label
              number
            }
            fuelRequirements
            fuelSystem
            gearcase
            gearShift
            id
            ignition
            induction
            maxRPM
            midsection
            mountingCenters
            oilCapacity
            partNumber
            postDate
            power
            price
            propulsion
            protectionSystem
            recommendedOil
            repowerEnabled
            slug
            starting
            steering
            title
            turbo
            type: typeHandle
            weight
          }
        }
        features {
          ... on features_feature_BlockType {
            id
            type: typeHandle
            dateCreated
            dateUpdated
            heading
            body
            benefit
            media {
              title
              id
              url
              kind
              size
              mediaSquareUrl: url @transform (transform: "mediaSquare")
            }
          }
        }
        mediaCollections {
          ... on mediaCollections_collection_BlockType {
            id
            type: typeHandle
            dateCreated
            dateUpdated
            heading
            media {
              id
              title
              kind
              size
              url
              mediaSquareUrl: url @transform (transform: "mediaSquare")
              ... on remote_Asset {
                summary
                videoEmbed {
                  ... on videoEmbed_BlockType {
                    videoUrl
                    width
                    height
                  }
                }
                thumbnail {
                  id
                  title
                  url @transform (transform: "mediaSquare")
                }
              }
            }
          }
        }
        newProductCategory {
          id
        }
      }
    }
  }`;

  const entry = await fetchWithFallback(
    queryBuilder,
    (response) => response.data.entry,
    `product details ${id}`
  );

  if (!entry) {
    log.error(`No product data found for id ${id}`);
    throw new Error(`No product data found for id ${id}`);
  }

  entry.hasDetail = true;
  log.info(`Loaded product data for id ${id}`, { language, entry });
  commit('SET_HOT_LOADED_PRODUCT_DETAILS', { language, entry });
}

/**
 * Resolves the product slug for the **current** Craft site only.
 * `loadIndividualProductDataById` may fall back to English and cache an EN slug under the active
 * locale; the router uses this for canonical `/product/:id-:slug` redirects.
 */
async function fetchCanonicalSlugForActiveLocale(context, { id }) {
  const site = localeToCraftSiteHandle(getActiveAppLanguage());
  const queryBuilder = (s) => `{
    entry(type: "products", site: "${s}", id: "${id}") {
      slug
    }
  }`;

  const executeQuery = (siteCode) => {
    const query = queryBuilder(siteCode);
    return api.get(constants.GRAPH_QL_ENDPOINT, {
      params: { query: toGraphQLQuery(query) },
      headers: getGraphQLHeaders(),
    });
  };

  try {
    const payload = await executeQuery(site);
    const dataBlock =
      payload &&
      payload.data &&
      (payload.data.data !== undefined ? payload.data.data : payload.data);
    const entry = dataBlock && dataBlock.entry;
    if (entry && entry.slug && String(entry.slug).trim().length > 0) {
      return String(entry.slug).trim();
    }
  } catch (error) {
    if (api.isCancel(error)) {
      return '';
    }
    log.warn(`fetchCanonicalSlugForActiveLocale failed for id ${id} site ${site}`, error);
  }

  return '';
}

async function loadProductCategories({ commit, state, getters }) {
  const queryBuilder = (site) => `{
    categories(group: "productCategories", site: "${site}") {
      id
      title
      slug
      lft
      rgt
      ... on productCategories_Category {
        mainImage {
          url @transform (transform: "mediaSquare")
        }
      }
      parent {
        id
      }
    }
  }`;

  const categories = await fetchWithFallback(
    queryBuilder,
    (response) => response.data.categories,
    'product categories'
  );
  
  if (categories) {
    log.info('Loaded product categories', { categories });
    commit(
      'SET_PRODUCT_CATEGORIES',
      flatToHierarchicalCategories(categories, getters),
    );
  }
}

// Create heirarchy object structure from a flat array
function flatToHierarchicalCategories(categories, getters) {
  if (!categories) {
    return [];
  }
  const categoriesById = categories.reduce((reduced, category) => {
    reduced[category.id] = category;
    return reduced;
  }, {});

  // connect children to parents
  var topLevelCategories = [];
  Object.values(categoriesById).forEach((category) => {
    const parentId = category.parent ? category.parent.id : null;
    if (parentId === null) {
      topLevelCategories.push(category);
    } else if (parentId in categoriesById) {
      const parent = categoriesById[parentId];
      if (!('children' in parent)) {
        parent.children = [];
      }
      parent.children.push(category);
    } else {
      log.info(
        `Category ${category.title} is missing its parent ${parentId}`,
      );
    }
  });

  // now sort and filter empty categories
  sortCategoriesInPlace(topLevelCategories);

  return topLevelCategories;
}

// Use the category sorting in Craft
function assignedHeirarchicalSort(category1, category2) {
  return category1.lft - category2.lft;
}

function sortCategoriesInPlace(categories) {
  if (!categories) {
    return;
  }
  categories.sort(assignedHeirarchicalSort);
  categories.forEach((category) => {
    sortCategoriesInPlace(category.children);
  });
}

async function loadMenuProducts({ commit, state }) {
  const queryBuilder = (site) => `{
    entries(relatedToCategories: {group: "productCategories"}, orderBy: "title", site: "${site}") {
      id
      title
      ... on products_products_Entry {
        flagAsNew
        categories: newProductCategory {
          id
          lft
          rgt
          parent {
            id
          }
        }
        mainImage {
          url @transform (transform: "mediaSquare")
        }
        specifications {
          ... on specifications_engines_Entry {
            id
            power
          }
        }
      }
    }
  }`;

  const entries = await fetchWithFallback(
    queryBuilder,
    (response) => response.data.entries,
    'menu products'
  );
  
  if (entries) {
    log.info('Loaded menu products', { entries });
    commit('SET_MENU_PRODUCTS', transformMenuProducts(entries));
  }
}

async function loadExcludedProducts({ commit, state }) {
  const regionId = store.getters[`${user.store.namespace}/regionId`];
  
  const queryBuilder = (site) => `{
    entry(type: "regions", id: "${regionId}", site: "${site}") {
      ... on locations_regions_Entry {
        excludedProducts {
          id
        }
      }
    }
  }`;

  const entry = await fetchWithFallback(
    queryBuilder,
    (response) => response.data.entry,
    'excluded products'
  );
  
  log.info('Loaded excluded products', { entry });
  const excludedProducts =
    entry && 'excludedProducts' in entry
      ? entry.excludedProducts
      : [];
  commit(
    'SET_EXCLUDED_PRODUCTS',
    excludedProducts.map((product) => product.id),
  );
}

// Find the deepest category and set it on the product
function transformMenuProducts(products) {
  if (!products || !Array.isArray(products)) {
    return [];
  }
  products.forEach((product) => {
    const topLevelCategories = flatToHierarchicalCategories(product.categories);
    if (topLevelCategories && topLevelCategories.length === 1) {
      var category = topLevelCategories[0];
      while ('children' in category) {
        if (category.children) category = category.children[0];
      }
      product.category = category.id;
    }
    product.minPower = getMinPower(product);
  });
  return products.sort((a, b) => {
    let diff = a.minPower - b.minPower;
    if (diff) {
      return diff;
    }
    return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  });
}

function getMinPower(product) {
  if ('specifications' in product && product.specifications.length) {
    return Math.min(
      ...product.specifications.map((specification) => {
        if (
          'power' in specification &&
          specification.power &&
          specification.power.length > 0
        ) {
          // Power entries look like "5 / 3.7" where the 5 is the horsepower
          const parts = specification.power.split('/');
          return parts.length ? parseFloat(parts[0].trim()) : 0.0;
        }
        return 0.0;
      }),
    );
  }
  return 0;
}

/**
 * Enrich the product with the new product category, in place of the old category property
 *
 * @param {Object} product
 * @returns {Object} The product with the new category
 */
function enrichProductCategory(product) {
  if (typeof product.category !== 'undefined' || !product.newProductCategory) {
    return product;
  }
  product.category = product.newProductCategory;
  return product;
}

function getProductById(state) {
  return (id) => {
    // log.debug(`[DEBUG] getProductById - ID ${id}: Entry point. state.products type: ${typeof state.products}, length: ${state.products ? state.products.length : 'N/A'}`);
    if (!state.products) {
      log.debug(`[DEBUG] getProductById - ID ${id}: Products array in store is null/undefined.`);
      return undefined;
    }

    let product = state.products.find(p => Number(p.id) === Number(id));
    if (product) {
      log.debug(`[DEBUG] getProductById - ID ${id}: Found in state.products. Raw Product:`, JSON.parse(JSON.stringify(product)));
      log.debug(`[DEBUG] getProductById - ID ${id}: Media Collections on raw product:`, product.mediaCollections ? JSON.parse(JSON.stringify(product.mediaCollections)) : product.mediaCollections);
    } else {
      log.debug(`[DEBUG] getProductById - ID ${id}: Not found in state.products using Number(p.id) === Number(id).`);
      // Attempt to find by string comparison as a fallback, due to potential type inconsistencies from GQL
      const productStr = state.products.find(p => String(p.id) === String(id));
      if (productStr) {
        log.debug(`[DEBUG] getProductById - ID ${id}: Found using String comparison! product.id was ${typeof productStr.id}, id was ${typeof id}. Product:`, JSON.parse(JSON.stringify(productStr)));
        product = productStr;
      }
    }

    if (product) {
      const enrichedProduct = enrichProductCategory(product, state.categories);
      if (enrichedProduct) {
        log.debug(`[DEBUG] getProductById - ID ${id}: After enrichProductCategory. Enriched Product:`, JSON.parse(JSON.stringify(enrichedProduct)));
        log.debug(`[DEBUG] getProductById - ID ${id}: Media Collections on enriched product:`, enrichedProduct.mediaCollections ? JSON.parse(JSON.stringify(enrichedProduct.mediaCollections)) : enrichedProduct.mediaCollections);
        return enrichedProduct;
      }
      // This case implies enrichProductCategory returned null/undefined from a valid product object, which might be an issue.
      log.debug(`[DEBUG] getProductById - ID ${id}: enrichProductCategory returned null/undefined from a previously valid product object.`);
      return undefined; // Or handle as an error, depending on desired behavior.
    }
    log.debug(`[DEBUG] getProductById - ID ${id}: Product was not found, so enrichProductCategory received undefined.`);
    return undefined;
  };
}

function REMOVE_PRODUCT(state, id) {
  if (!state.products) {
    return;
  }
  state.products = state.products.filter((obj) => {
    return obj.id !== id;
  });
}

function removeProduct({ commit }, id) {
  return commit('REMOVE_PRODUCT', id);
}

function getCategoryById(state) {
  return (id, withChildren) => {
    return findCategory(id, state.categories, withChildren);
  };
}

function findProductsUsingAssetId(state) {
  return (assetId) => {
    assetId = parseInt(assetId);
    if (!state.products) {
      return [];
    }
    return state.products.filter((product) => {
      const hasAsset = product.mediaCollections.some((mc) => {
        return mc.media.some((m) => parseInt(m.id) === assetId);
      });

      return hasAsset;
    });
  };
}

function getMenuProductsByCategory(state) {
  return (categoryId) => {
    if (!state.menuProducts) {
      return [];
    }

    // First filter to products in the category
    const products = state.menuProducts.filter((product) => {
      return product.category === categoryId;
    });

    // Then remove excluded products
    if (state.excludedProducts && state.excludedProducts.length > 0) {
      return products.filter((product) => {
        return !state.excludedProducts.includes(product.id);
      });
    }
    return products;
  };
}

function getProductsByCategory(state) {
  return (categoryId) => {
    if (!state.products) {
      return [];
    }

    function getMinPower(product) {
      if (!product.specifications) {
        return 0;
      }

      let powers = product.specifications
        .map((s) => {
          if (!s.power) {
            return 0.0;
          }
          const parts = s.power.split('/');
          return parts[0] ? parseFloat(parts[0]) : 0.0;
        })
        .filter((p) => p > 0)
        .sort((a, b) => a - b);

      return powers ? powers[0] : 0.0;
    }

    function byString(a, b) {
      let upa = a ? a.toUpperCase() : '';
      let upb = b ? b.toUpperCase() : '';
      if (upa < upb) return -1;
      if (upa > upb) return 1;
      return 0;
    }

    return state.products
      .filter((product) => {
        return product.productCategory === categoryId;
      })
      .sort((a, b) => {
        const pa = getMinPower(a);
        const pb = getMinPower(b);
        if (pa > 0 || pb > 0) {
          return pa - pb;
        } else {
          return byString(a.title, b.title);
        }
      });
  };
}

function getFilteredCategories(state, getters) {
  if (!state.internalProductCategories) {
    return [];
  }
  // If we don't have excluded products, don't do the subcategory removal dance
  if (!state.excludedProducts || state.excludedProducts.length == 0) {
    return state.internalProductCategories;
  }
  return state.internalProductCategories.map((category) =>
    removeEmptySubCategories(category, getters, true),
  );
}

function removeEmptySubCategories(category, getters, needsClone) {
  // Don't modify Vuex state in a getter, so we do a deep clone
  const workingCategory = needsClone
    ? JSON.parse(JSON.stringify(category))
    : category;
  if (workingCategory.children && workingCategory.children.length > 0) {
    workingCategory.children = workingCategory.children.map((child) =>
      removeEmptySubCategories(child, getters, false),
    );

    workingCategory.children = workingCategory.children.filter(
      (childCategory) => {
        const hasChildCategories =
          childCategory.children && childCategory.children.length > 0;
        const hasProducts = getters.hasMenuProductsInCategory(childCategory.id);
        return hasChildCategories || hasProducts;
      },
    );
  }
  return workingCategory;
}

function hasMenuProductsInCategory(state) {
  return (categoryId) => {
    if (!state.menuProducts) {
      return false;
    }

    // First filter out excluded products
    const filteredProducts = state.excludedProducts
      ? state.menuProducts.filter((product) => {
          return !state.excludedProducts.includes(product.id);
        })
      : state.menuProducts;

    // Then search for any product in the category
    return filteredProducts.some((product) => {
      return product.category === categoryId;
    });
  };
}

function findCategory(id, categories, withChildren) {
  if (!categories || !categories.length || isNaN(id)) {
    return;
  }

  for (let i = 0; i < categories.length; i++) {
    if (categories[i].id === id) {
      const clone = { ...categories[i] };
      if (!withChildren) {
        clone.parents = [];
      }
      return clone;
    } else {
      let foundInChildren = findCategory(id, categories[i].children);

      if (foundInChildren) {
        const clone = { ...categories[i] };
        if (!withChildren) {
          delete clone.children;
          foundInChildren.parents.push(clone);
        }
        return foundInChildren;
      }
    }
  }
}

function categoryUrlSlug(state) {
  return (category) => {
    if (!category) {
      return '';
    }
    const nameSlug = slugify(category.title, {
      lower: true,
    });
    return `${category.id}-${nameSlug}`;
  };
}
function getRootParentTitleCategoryById(state) {
  return (id) => {
    const category = getCategoryById(state)(id);

    if (!category) {
      log.info(`No category found with ID: ${id}`);
      return;
    }

    return findRootParentCategory(category);
  };
}

function findRootParentCategory(category) {
  if (!category) {
    log.error('Category is not provided');
    return;
  }

  if (category.parents && category.parents.length > 0) {
    return category.parents[category.parents.length - 1];
  } else {
    return category;
  }
}
async function loadProductsOverview({ state, commit }) {
  const queryBuilder = (site) => `{
    entry(section: "productsOverview", site: "${site}") {
      ... on productsOverview_productsOverview_Entry {
        productsOverviewMarqueeHeadline
        productsOverviewMarqueeSubhead
        productsOverviewMarqueeImage {
          url @transform (width: 1400)
        }
        productsOverviewTrainingCTAHeadline
        productsOverviewTrainingCTAText
        productsOverviewTrainingCTAImage {
          url @transform (width: 1400)
        }
        productsOverviewTrainingCTALinkText
      }
    }
  }`;

  const data = await fetchWithFallback(
    queryBuilder,
    (response) => response.data.entry,
    'products overview'
  );
  
  if (data) {
    log.info('Loaded product overview data', { data });
    commit('SET_PRODUCTS_OVERVIEW', data);
  }
}
